import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SendIcon from "@mui/icons-material/Send";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { LENDTECH_QUEUE_PATHS } from "./loanMehrsanStageConstants";

const DEFAULT_LENDTECH_CODE = "IRIA_PAY";

const resolvePartnerId = (partner) => partner?.id ?? partner?.Id ?? null;

const resolvePartnerCode = (partner) =>
  String(partner?.code ?? partner?.Code ?? "").toUpperCase();

const resolvePartnerLabel = (partner) => {
  const title = partner?.title ?? partner?.Title ?? "";
  const code = partner?.code ?? partner?.Code ?? "";
  if (title && code) return `${title} (${code})`;
  return title || code || "لندتک";
};

const resolveDefaultPartnerId = (partners = []) => {
  const iria = partners.find(
    (partner) => resolvePartnerCode(partner) === DEFAULT_LENDTECH_CODE
  );
  return resolvePartnerId(iria) ?? resolvePartnerId(partners[0]);
};

const buildQueuePath = (partnerCode, searchHint) => {
  const base = LENDTECH_QUEUE_PATHS[partnerCode];
  if (!base) return null;
  const q = String(searchHint || "").trim();
  if (!q) return base;
  return `${base}?q=${encodeURIComponent(q)}`;
};

const LendtechSelectionCard = ({
  partners = [],
  lendingPartnerId = null,
  lendingPartnerCode = "",
  lendingPartnerLabel = "",
  queueSearchHint = "",
  canEdit = false,
  sending = false,
  changingPartner = false,
  nextBusy = false,
  canNext = false,
  onSend,
  onNext,
  onChangePartner,
  autoCreditInquirySubmitted = false,
}) => {
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [changePartnerId, setChangePartnerId] = useState("");
  const [changeReason, setChangeReason] = useState("");

  const iriaPartner = useMemo(
    () =>
      (partners || []).find(
        (partner) => resolvePartnerCode(partner) === DEFAULT_LENDTECH_CODE
      ) || null,
    [partners]
  );
  const iriaPartnerId = resolvePartnerId(iriaPartner);
  const otherPartners = useMemo(
    () =>
      (partners || []).filter(
        (partner) => resolvePartnerCode(partner) !== DEFAULT_LENDTECH_CODE
      ),
    [partners]
  );

  const changeablePartners = useMemo(
    () =>
      (partners || []).filter(
        (partner) =>
          Number(resolvePartnerId(partner)) !== Number(lendingPartnerId || 0)
      ),
    [partners, lendingPartnerId]
  );

  useEffect(() => {
    if (selectedPartnerId) return;
    const defaultId = resolveDefaultPartnerId(partners);
    if (defaultId) setSelectedPartnerId(String(defaultId));
  }, [partners, selectedPartnerId]);

  useEffect(() => {
    if (!changeDialogOpen) return;
    const preferred =
      changeablePartners.find(
        (p) => resolvePartnerCode(p) === DEFAULT_LENDTECH_CODE
      ) || changeablePartners[0];
    const preferredId = resolvePartnerId(preferred);
    setChangePartnerId(preferredId ? String(preferredId) : "");
    setChangeReason("");
  }, [changeDialogOpen, changeablePartners]);

  const queuePath = useMemo(
    () => buildQueuePath(lendingPartnerCode, queueSearchHint),
    [lendingPartnerCode, queueSearchHint]
  );

  const handleConfirmChange = async () => {
    const partnerId = Number(changePartnerId);
    const reason = String(changeReason || "").trim();
    if (!partnerId || !reason || typeof onChangePartner !== "function") return;
    try {
      await onChangePartner(partnerId, reason);
      setChangeDialogOpen(false);
      setChangeReason("");
    } catch {
      // parent shows toast; keep dialog open for retry
    }
  };

  const changePartnerDialog = (
    <Dialog
      open={changeDialogOpen}
      onClose={() => !changingPartner && setChangeDialogOpen(false)}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>تغییر لندتک</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {lendingPartnerLabel ? (
            <Typography variant="body2" color="text.secondary">
              لندتک فعلی: <strong>{lendingPartnerLabel}</strong>
            </Typography>
          ) : null}
          <FormControl fullWidth size="small">
            <InputLabel id="change-lendtech-partner-label">لندتک جدید</InputLabel>
            <Select
              labelId="change-lendtech-partner-label"
              label="لندتک جدید"
              value={changePartnerId}
              onChange={(e) => setChangePartnerId(String(e.target.value))}
              disabled={changingPartner || changeablePartners.length === 0}
            >
              {changeablePartners.map((partner) => {
                const id = resolvePartnerId(partner);
                return (
                  <MenuItem key={id} value={String(id)}>
                    {resolvePartnerLabel(partner)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <TextField
            label="دلیل تغییر"
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
            required
            fullWidth
            size="small"
            multiline
            minRows={2}
            disabled={changingPartner}
            helperText="ثبت دلیل برای تاریخچه تغییر لندتک الزامی است."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setChangeDialogOpen(false)}
          disabled={changingPartner}
        >
          انصراف
        </Button>
        <Button
          variant="contained"
          color="warning"
          disabled={
            changingPartner ||
            !changePartnerId ||
            !String(changeReason || "").trim()
          }
          onClick={handleConfirmChange}
          startIcon={
            changingPartner ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <SwapHorizIcon />
            )
          }
        >
          {changingPartner ? "در حال تغییر..." : "ثبت تغییر"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (lendingPartnerLabel) {
    const isIria =
      String(lendingPartnerCode || "").toUpperCase() === DEFAULT_LENDTECH_CODE;

    return (
      <>
        <Alert severity="success" variant="outlined">
          <Stack spacing={1.25}>
            <Typography variant="body2" fontWeight={600}>
              پرونده به {lendingPartnerLabel} ارسال شده است.
            </Typography>
            <Typography variant="body2">
              {isIria
                ? "برای ادامه، همین‌جا «مرحله بعد» را بزنید یا صف ایریاپی را باز کنید."
                : "پیگیری مراحل بعدی در صفحه عملیات همان لندتک انجام می‌شود."}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {isIria && canNext ? (
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  disabled={nextBusy}
                  onClick={() => onNext?.()}
                  startIcon={
                    nextBusy ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <SkipNextIcon />
                    )
                  }
                >
                  {nextBusy ? "در حال اجرا..." : "مرحله بعد در ایریاپی"}
                </Button>
              ) : null}
              {queuePath ? (
                <Button
                  size="small"
                  variant="outlined"
                  component={RouterLink}
                  to={queuePath}
                  startIcon={<OpenInNewIcon />}
                >
                  باز کردن صف {isIria ? "ایریاپی" : "لندتک"}
                </Button>
              ) : null}
              {canEdit && typeof onChangePartner === "function" ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={changingPartner || changeablePartners.length === 0}
                  onClick={() => setChangeDialogOpen(true)}
                  startIcon={<SwapHorizIcon />}
                >
                  تغییر لندتک
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Alert>
        {changePartnerDialog}
      </>
    );
  }

  if (!canEdit) {
    return (
      <Alert severity="warning" variant="outlined">
        هنوز لندتکی برای این پرونده انتخاب نشده است.
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack spacing={1.5}>
        <Typography variant="subtitle2" fontWeight={700}>
          ارسال پرونده به لندتک
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {autoCreditInquirySubmitted
            ? "پرونده هنگام ثبت درخواست به‌صورت خودکار به ایریاپی ارسال شده است."
            : "معمولاً پرونده‌ها به ایریاپی ارسال می‌شوند. با یک کلیک ثبت در ایریا و شروع فرآیند وام انجام می‌شود."}
        </Typography>

        {autoCreditInquirySubmitted ? (
          <Alert severity="info" variant="outlined">
            ارسال دستی به ایریاپی غیرفعال است؛ از صف ایریاپی یا «استعلام مجدد» در بررسی اولیه
            استفاده کنید.
          </Alert>
        ) : null}

        {!autoCreditInquirySubmitted && iriaPartnerId ? (
          <Button
            variant="contained"
            color="secondary"
            size="large"
            startIcon={
              sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />
            }
            disabled={sending}
            onClick={() => onSend?.(Number(iriaPartnerId))}
            sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}
          >
            {sending ? "در حال ارسال به ایریاپی..." : "ارسال به ایریاپی"}
          </Button>
        ) : null}

        {otherPartners.length > 0 ? (
          <Accordion disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight={600}>
                ارسال به لندتک دیگر
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                <FormControl fullWidth size="small" sx={{ maxWidth: 360 }}>
                  <InputLabel id="workspace-lendtech-other-label">لندتک</InputLabel>
                  <Select
                    labelId="workspace-lendtech-other-label"
                    label="لندتک"
                    value={
                      otherPartners.some(
                        (p) => String(resolvePartnerId(p)) === String(selectedPartnerId)
                      )
                        ? selectedPartnerId
                        : String(resolvePartnerId(otherPartners[0]) || "")
                    }
                    onChange={(e) => setSelectedPartnerId(String(e.target.value))}
                    disabled={sending}
                  >
                    {otherPartners.map((partner) => {
                      const id = resolvePartnerId(partner);
                      return (
                        <MenuItem key={id} value={String(id)}>
                          {resolvePartnerLabel(partner)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={
                    sending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SendIcon />
                    )
                  }
                  disabled={
                    sending ||
                    !selectedPartnerId ||
                    String(selectedPartnerId) === String(iriaPartnerId)
                  }
                  onClick={() => onSend?.(Number(selectedPartnerId))}
                >
                  ارسال
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>
        ) : !iriaPartnerId ? (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
            <FormControl fullWidth size="small" sx={{ maxWidth: 360 }}>
              <InputLabel id="workspace-lendtech-partner-label">لندتک</InputLabel>
              <Select
                labelId="workspace-lendtech-partner-label"
                label="لندتک"
                value={selectedPartnerId}
                onChange={(e) => setSelectedPartnerId(String(e.target.value))}
                disabled={sending || partners.length === 0}
              >
                {partners.map((partner) => {
                  const id = resolvePartnerId(partner);
                  return (
                    <MenuItem key={id} value={String(id)}>
                      {resolvePartnerLabel(partner)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="secondary"
              startIcon={
                sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />
              }
              disabled={sending || !selectedPartnerId}
              onClick={() => onSend?.(Number(selectedPartnerId))}
            >
              {sending ? "در حال ثبت..." : "ارسال به لندتک"}
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
};

LendtechSelectionCard.propTypes = {
  partners: PropTypes.arrayOf(PropTypes.object),
  lendingPartnerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  lendingPartnerCode: PropTypes.string,
  lendingPartnerLabel: PropTypes.string,
  queueSearchHint: PropTypes.string,
  canEdit: PropTypes.bool,
  sending: PropTypes.bool,
  changingPartner: PropTypes.bool,
  nextBusy: PropTypes.bool,
  canNext: PropTypes.bool,
  onSend: PropTypes.func,
  onNext: PropTypes.func,
  onChangePartner: PropTypes.func,
  autoCreditInquirySubmitted: PropTypes.bool,
};

export default LendtechSelectionCard;
